import { useState, useEffect, useMemo } from 'react';
import {
  Wand2,
  Plus,
  Search,
  Sparkles,
  Layers,
  Edit2,
  CheckCircle2,
  XCircle,
  X,
  RefreshCw,
  Info,
  DollarSign,
  Activity,
  Clock,
  Receipt,
  Eye,
} from 'lucide-react';
import {
  aiStylesService,
  type AiArtifactStyle,
  type CreateAiStylePayload,
  type CanonicalAspectRatio,
  ASPECT_RATIOS,
  type AiGenerationStats,
  type AiGenerationLog,
} from '../services/aiStylesService';

export function AiStylesPage() {
  const [styles, setStyles] = useState<AiArtifactStyle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOccasion, setSelectedOccasion] = useState<string>('all');

  // Provider Cost & Stats State
  const [genStats, setGenStats] = useState<AiGenerationStats | null>(null);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logs, setLogs] = useState<AiGenerationLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStyle, setEditingStyle] = useState<AiArtifactStyle | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form Fields
  const [formId, setFormId] = useState('');
  const [formName, setFormName] = useState('');
  const [formOccasion, setFormOccasion] = useState<'wedding' | 'birthday'>('wedding');
  const [formPromptTemplate, setFormPromptTemplate] = useState('');
  const [formThumbnailUrl, setFormThumbnailUrl] = useState('');
  const [formTextRenderMode, setFormTextRenderMode] = useState<'overlay' | 'baked'>('overlay');
  const [formImageProvider, setFormImageProvider] = useState<'pulid-flux' | 'gemini' | 'openai'>(
    'pulid-flux',
  );
  const [formMaxFaces, setFormMaxFaces] = useState<number>(2);
  const [formActive, setFormActive] = useState<boolean>(true);
  const [formAllowedRatios, setFormAllowedRatios] = useState<CanonicalAspectRatio[]>(['square']);
  const [formDefaultRatio, setFormDefaultRatio] = useState<CanonicalAspectRatio>('square');

  const RATIO_LABELS: Record<CanonicalAspectRatio, { label: string; dims: string }> = {
    square:    { label: 'Square',    dims: '1:1 · 1080×1080' },
    portrait:  { label: 'Portrait',  dims: '4:5 · 1080×1350' },
    landscape: { label: 'Landscape', dims: '16:9 · 1920×1080' },
    story:     { label: 'Story',     dims: '9:16 · 1080×1920' },
  };

  const handleRatioCheck = (ratio: CanonicalAspectRatio, checked: boolean) => {
    setFormAllowedRatios((prev) => {
      const next = checked ? [...prev, ratio] : prev.filter((r) => r !== ratio);
      // If we just unchecked the current default, pick the first remaining
      if (!checked && formDefaultRatio === ratio) {
        const fallback = next[0] ?? 'square';
        setFormDefaultRatio(fallback);
      }
      // Ensure at least one ratio is always checked
      return next.length === 0 ? ['square'] : next;
    });
  };

  const fetchStyles = async () => {
    setLoading(true);
    setError(null);
    try {
      const [stylesData, statsData] = await Promise.all([
        aiStylesService.getAllStyles(),
        aiStylesService.fetchStats().catch(() => null),
      ]);
      setStyles(stylesData);
      if (statsData) setGenStats(statsData);
    } catch (err: any) {
      setError(err.message || 'Failed to load AI styles');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenLogs = async () => {
    setShowLogsModal(true);
    setLogsLoading(true);
    try {
      const res = await aiStylesService.fetchGenerations(1, 50);
      setLogs(res.items);
    } catch (err: any) {
      alert(`Failed to load generation logs: ${err.message}`);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    fetchStyles();
  }, []);

  const handleOpenCreateModal = () => {
    setEditingStyle(null);
    setFormId('');
    setFormName('');
    setFormOccasion('wedding');
    setFormPromptTemplate('');
    setFormThumbnailUrl('');
    setFormTextRenderMode('overlay');
    setFormImageProvider('pulid-flux');
    setFormMaxFaces(2);
    setFormActive(true);
    setFormAllowedRatios(['square']);
    setFormDefaultRatio('square');
    setFormError(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (style: AiArtifactStyle) => {
    setEditingStyle(style);
    setFormId(style.id);
    setFormName(style.name);
    setFormOccasion((style.occasion as 'wedding' | 'birthday') || 'wedding');
    setFormPromptTemplate(style.promptTemplate);
    setFormThumbnailUrl(style.thumbnailUrl || '');
    setFormTextRenderMode((style.defaultTextRenderMode as 'overlay' | 'baked') || 'overlay');
    setFormImageProvider(
      (style.imageProvider as 'pulid-flux' | 'gemini' | 'openai') || 'pulid-flux',
    );
    setFormMaxFaces(style.maxFaces || 1);
    setFormActive(style.active);
    setFormAllowedRatios(style.allowedAspectRatios ?? ['square']);
    setFormDefaultRatio(style.defaultAspectRatio ?? 'square');
    setFormError(null);
    setIsModalOpen(true);
  };

  const handleSaveStyle = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formId.trim()) {
      setFormError('Style ID is required (e.g. wedding-royal-gold)');
      return;
    }
    if (!formName.trim()) {
      setFormError('Style Name is required');
      return;
    }
    if (!formPromptTemplate.trim()) {
      setFormError('Prompt Template is required');
      return;
    }

    setSubmitting(true);
    try {
      if (editingStyle) {
        await aiStylesService.updateStyle(editingStyle.id, {
          occasion: formOccasion,
          name: formName,
          promptTemplate: formPromptTemplate,
          thumbnailUrl: formThumbnailUrl.trim() || undefined,
          defaultTextRenderMode: formTextRenderMode,
          imageProvider: formImageProvider,
          maxFaces: Number(formMaxFaces),
          active: formActive,
          allowedAspectRatios: formAllowedRatios,
          defaultAspectRatio: formDefaultRatio,
        });
      } else {
        const payload: CreateAiStylePayload = {
          id: formId.trim().toLowerCase(),
          occasion: formOccasion,
          name: formName.trim(),
          promptTemplate: formPromptTemplate.trim(),
          thumbnailUrl: formThumbnailUrl.trim() || undefined,
          defaultTextRenderMode: formTextRenderMode,
          imageProvider: formImageProvider,
          maxFaces: Number(formMaxFaces),
          active: formActive,
          allowedAspectRatios: formAllowedRatios,
          defaultAspectRatio: formDefaultRatio,
        };
        await aiStylesService.createStyle(payload);
      }

      setIsModalOpen(false);
      await fetchStyles();
    } catch (err: any) {
      setFormError(err.message || 'Failed to save style');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (style: AiArtifactStyle) => {
    try {
      await aiStylesService.toggleActive(style.id);
      setStyles((prev) =>
        prev.map((s) => (s.id === style.id ? { ...s, active: !s.active } : s)),
      );
    } catch (err: any) {
      alert(`Failed to toggle active status: ${err.message}`);
    }
  };

  const filteredStyles = useMemo(() => {
    return styles.filter((style) => {
      const matchesOccasion =
        selectedOccasion === 'all' || style.occasion === selectedOccasion;
      const matchesSearch =
        style.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        style.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        style.promptTemplate.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesOccasion && matchesSearch;
    });
  }, [styles, selectedOccasion, searchQuery]);

  const stats = useMemo(() => {
    const total = styles.length;
    const active = styles.filter((s) => s.active).length;
    const wedding = styles.filter((s) => s.occasion === 'wedding').length;
    const birthday = styles.filter((s) => s.occasion === 'birthday').length;
    return { total, active, wedding, birthday };
  }, [styles]);

  return (
    <div className="space-y-8">
      {/* Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-surface-container-low p-6 rounded-2xl border border-border-subtle shadow-sm">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2.5 rounded-xl bg-primary/10 text-primary">
              <Wand2 className="w-6 h-6" />
            </div>
            <h1 className="text-2xl font-bold text-on-surface">AI Invitation Styles & Cost Analytics</h1>
          </div>
          <p className="text-on-surface-variant text-sm">
            Admin registry and live cost tracking for AI image invitations (fal.ai, OpenAI, Gemini). Manage prompts, cost stats, and provider execution logs.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleOpenLogs}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-surface-container-high text-on-surface font-medium hover:bg-surface-container-highest border border-border-subtle transition-colors shadow-sm text-sm"
          >
            <Receipt className="w-4 h-4 text-emerald-400" />
            <span>Audit Logs & Costs</span>
          </button>
          <button
            onClick={handleOpenCreateModal}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-on-primary font-medium hover:bg-primary/90 transition-colors shadow-sm text-sm"
          >
            <Plus className="w-4 h-4" />
            <span>Create New Style</span>
          </button>
        </div>
      </div>

      {/* Live Provider Cost & Analytics Banner */}
      {genStats && (
        <div className="bg-gradient-to-r from-emerald-950/20 via-surface-container-low to-purple-950/20 p-5 rounded-2xl border border-emerald-500/20 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-on-surface flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              <span>Live Provider Cost & Latency Metrics</span>
            </h2>
            <span className="text-xs text-on-surface-variant font-mono">Real-Time DB Tracking</span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-3.5 rounded-xl bg-surface-container-lowest border border-border-subtle">
              <div className="text-xs text-on-surface-variant flex items-center gap-1.5 mb-1 font-medium">
                <DollarSign className="w-3.5 h-3.5 text-emerald-400" />
                Est. Total Cost (USD)
              </div>
              <div className="text-xl font-bold text-emerald-400">
                ${genStats.totalCostUsd.toFixed(4)}
              </div>
            </div>

            <div className="p-3.5 rounded-xl bg-surface-container-lowest border border-border-subtle">
              <div className="text-xs text-on-surface-variant flex items-center gap-1.5 mb-1 font-medium">
                <Sparkles className="w-3.5 h-3.5 text-primary" />
                Total Generations
              </div>
              <div className="text-xl font-bold text-on-surface">
                {genStats.totalGenerations} <span className="text-xs font-normal text-emerald-400">({genStats.readyCount} ready)</span>
              </div>
            </div>

            <div className="p-3.5 rounded-xl bg-surface-container-lowest border border-border-subtle">
              <div className="text-xs text-on-surface-variant flex items-center gap-1.5 mb-1 font-medium">
                <Activity className="w-3.5 h-3.5 text-emerald-400" />
                Success Rate
              </div>
              <div className="text-xl font-bold text-on-surface">
                {genStats.successRate}%
              </div>
            </div>

            <div className="p-3.5 rounded-xl bg-surface-container-lowest border border-border-subtle">
              <div className="text-xs text-on-surface-variant flex items-center gap-1.5 mb-1 font-medium">
                <Clock className="w-3.5 h-3.5 text-amber-400" />
                Avg Latency
              </div>
              <div className="text-xl font-bold text-on-surface">
                {genStats.avgLatencyMs > 0 ? `${(genStats.avgLatencyMs / 1000).toFixed(1)}s` : 'N/A'}
              </div>
            </div>
          </div>

          {/* Provider Breakdown Badges */}
          {Object.keys(genStats.providerStats).length > 0 && (
            <div className="pt-2 border-t border-border-subtle/50 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-on-surface-variant font-medium">Provider Cost Breakdown:</span>
              {Object.entries(genStats.providerStats).map(([pName, pStat]) => (
                <span
                  key={pName}
                  className="px-2.5 py-1 rounded-lg bg-surface-container-high border border-border-subtle text-on-surface font-mono text-[11px]"
                >
                  <strong className="text-primary">{pName}</strong>: {pStat.count} runs (<span className="text-emerald-400">${pStat.costUsd.toFixed(4)}</span>)
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="p-4 rounded-xl bg-surface-container-lowest border border-border-subtle">
          <div className="flex items-center gap-2 text-on-surface-variant text-xs font-medium mb-1">
            <Layers className="w-4 h-4 text-primary" />
            Total Styles
          </div>
          <div className="text-2xl font-bold text-on-surface">{stats.total}</div>
        </div>
        <div className="p-4 rounded-xl bg-surface-container-lowest border border-border-subtle">
          <div className="flex items-center gap-2 text-on-surface-variant text-xs font-medium mb-1">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            Active Styles
          </div>
          <div className="text-2xl font-bold text-emerald-500">{stats.active}</div>
        </div>
        <div className="p-4 rounded-xl bg-surface-container-lowest border border-border-subtle">
          <div className="flex items-center gap-2 text-on-surface-variant text-xs font-medium mb-1">
            <Sparkles className="w-4 h-4 text-amber-500" />
            Wedding
          </div>
          <div className="text-2xl font-bold text-on-surface">{stats.wedding}</div>
        </div>
        <div className="p-4 rounded-xl bg-surface-container-lowest border border-border-subtle">
          <div className="flex items-center gap-2 text-on-surface-variant text-xs font-medium mb-1">
            <Sparkles className="w-4 h-4 text-purple-500" />
            Birthday
          </div>
          <div className="text-2xl font-bold text-on-surface">{stats.birthday}</div>
        </div>
      </div>

      {/* Controls & Search */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
        {/* Occasion Filter Pills */}
        <div className="flex items-center gap-1.5 p-1 rounded-xl bg-surface-container-lowest border border-border-subtle">
          {(['all', 'wedding', 'birthday'] as const).map((occ) => (
            <button
              key={occ}
              onClick={() => setSelectedOccasion(occ)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                selectedOccasion === occ
                  ? 'bg-primary text-on-primary shadow-xs'
                  : 'text-on-surface-variant hover:text-on-surface'
              }`}
            >
              {occ}
            </button>
          ))}
        </div>

        {/* Search & Refresh */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-on-surface-variant" />
            <input
              type="text"
              placeholder="Search style or prompt..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-xs rounded-xl bg-surface-container-lowest border border-border-subtle text-on-surface focus:outline-none focus:border-primary"
            />
          </div>

          <button
            onClick={fetchStyles}
            title="Refresh styles"
            className="p-2.5 rounded-xl bg-surface-container-lowest border border-border-subtle text-on-surface-variant hover:text-on-surface transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="p-4 rounded-xl bg-error-container text-on-error-container text-sm flex items-center gap-2">
          <XCircle className="w-5 h-5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Grid of Styles */}
      {loading ? (
        <div className="py-20 text-center text-on-surface-variant text-sm">
          Loading AI Styles...
        </div>
      ) : filteredStyles.length === 0 ? (
        <div className="py-20 text-center bg-surface-container-lowest rounded-2xl border border-border-subtle p-8">
          <Wand2 className="w-10 h-10 text-on-surface-variant mx-auto mb-3 opacity-50" />
          <h3 className="text-base font-semibold text-on-surface mb-1">No AI Styles Found</h3>
          <p className="text-xs text-on-surface-variant mb-4">
            No styles match your search filters or none have been created yet.
          </p>
          <button
            onClick={handleOpenCreateModal}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-on-primary text-xs font-medium"
          >
            <Plus className="w-4 h-4" />
            Create First Style
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredStyles.map((style) => (
            <div
              key={style.id}
              className={`flex flex-col rounded-2xl bg-surface-container-lowest border transition-all overflow-hidden ${
                style.active
                  ? 'border-border-subtle hover:border-primary/40 shadow-sm'
                  : 'border-border-subtle/50 opacity-70 bg-surface-container-low/30'
              }`}
            >
              {/* Thumbnail Header */}
              <div className="relative h-40 bg-surface-container-low overflow-hidden">
                {style.thumbnailUrl ? (
                  <img
                    src={style.thumbnailUrl}
                    alt={style.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-on-surface-variant/40">
                    <Sparkles className="w-12 h-12" />
                  </div>
                )}

                <div className="absolute top-3 left-3 flex items-center gap-1.5">
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-wide uppercase bg-black/60 text-white backdrop-blur-md">
                    {style.occasion}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary/80 text-white backdrop-blur-md">
                    {style.maxFaces} Face{style.maxFaces > 1 ? 's' : ''}
                  </span>
                </div>

                <div className="absolute top-3 right-3">
                  <button
                    onClick={() => handleToggleActive(style)}
                    title={style.active ? 'Deactivate Style' : 'Activate Style'}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-semibold flex items-center gap-1 backdrop-blur-md transition-colors ${
                      style.active
                        ? 'bg-emerald-500/90 text-white'
                        : 'bg-zinc-700/90 text-zinc-300'
                    }`}
                  >
                    {style.active ? (
                      <>
                        <CheckCircle2 className="w-3 h-3" /> Active
                      </>
                    ) : (
                      <>
                        <XCircle className="w-3 h-3" /> Inactive
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Body Details */}
              <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <h3 className="text-base font-bold text-on-surface line-clamp-1">
                      {style.name}
                    </h3>
                  </div>
                  <div className="text-[11px] font-mono text-on-surface-variant/70 mb-3">
                    ID: {style.id}
                  </div>

                  <div className="p-3 rounded-xl bg-surface-container-low border border-border-subtle/50 text-xs text-on-surface-variant font-mono line-clamp-3 leading-relaxed">
                    {style.promptTemplate}
                  </div>
                </div>

                <div className="pt-2 border-t border-border-subtle/50 space-y-1">
                  <div className="flex items-center justify-between text-xs text-on-surface-variant">
                    <span className="capitalize">
                      Mode: <strong className="text-on-surface">{style.defaultTextRenderMode}</strong>
                      {' · '}
                      <strong className="text-on-surface">{style.imageProvider || 'pulid-flux'}</strong>
                    </span>
                    <button
                      onClick={() => handleOpenEditModal(style)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-surface-container-low hover:bg-surface-container text-on-surface font-medium transition-colors border border-border-subtle"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                      <span>Edit</span>
                    </button>
                  </div>
                  {/* Aspect ratio badges */}
                  <div className="flex flex-wrap gap-1 pt-1">
                    {(style.allowedAspectRatios ?? ['square']).map((r) => (
                      <span
                        key={r}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize ${
                          r === (style.defaultAspectRatio ?? 'square')
                            ? 'bg-primary/20 text-primary ring-1 ring-primary/40'
                            : 'bg-surface-container-low text-on-surface-variant'
                        }`}
                      >
                        {r}{r === (style.defaultAspectRatio ?? 'square') ? ' ★' : ''}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Dialog for Create/Edit */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-xl bg-surface-container-lowest rounded-2xl border border-border-subtle shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-border-subtle bg-surface-container-low">
              <div className="flex items-center gap-2.5">
                <Wand2 className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-bold text-on-surface">
                  {editingStyle ? 'Edit AI Style' : 'Create New AI Style'}
                </h2>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1 rounded-lg text-on-surface-variant hover:text-on-surface"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSaveStyle} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
              {formError && (
                <div className="p-3 rounded-xl bg-error-container text-on-error-container text-xs flex items-center gap-2">
                  <XCircle className="w-4 h-4 shrink-0" />
                  <span>{formError}</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-on-surface mb-1">
                    Style ID Slug *
                  </label>
                  <input
                    type="text"
                    disabled={!!editingStyle}
                    placeholder="e.g. wedding-royal-gold"
                    value={formId}
                    onChange={(e) => setFormId(e.target.value)}
                    className="w-full px-3 py-2 text-xs rounded-xl bg-surface-container-low border border-border-subtle text-on-surface focus:outline-none focus:border-primary disabled:opacity-50 font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-on-surface mb-1">
                    Occasion *
                  </label>
                  <select
                    value={formOccasion}
                    onChange={(e) => setFormOccasion(e.target.value as 'wedding' | 'birthday')}
                    className="w-full px-3 py-2 text-xs rounded-xl bg-surface-container-low border border-border-subtle text-on-surface focus:outline-none focus:border-primary"
                  >
                    <option value="wedding">Wedding</option>
                    <option value="birthday">Birthday</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-on-surface mb-1">
                  Style Name *
                </label>
                <input
                  type="text"
                  placeholder="e.g. Royal Gold Heritage"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-xl bg-surface-container-low border border-border-subtle text-on-surface focus:outline-none focus:border-primary"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-on-surface">
                    Prompt Template *
                  </label>
                  <span className="text-[10px] text-on-surface-variant flex items-center gap-1">
                    <Info className="w-3 h-3" /> Use {'{couple_description}'} or {'{person_description}'}
                  </span>
                </div>
                <textarea
                  rows={4}
                  placeholder="A breathtaking portrait featuring {couple_description} in rich royal gold attire..."
                  value={formPromptTemplate}
                  onChange={(e) => setFormPromptTemplate(e.target.value)}
                  className="w-full p-3 text-xs font-mono rounded-xl bg-surface-container-low border border-border-subtle text-on-surface focus:outline-none focus:border-primary leading-relaxed"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-on-surface mb-1">
                    Max Faces (Photo Count)
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={5}
                    value={formMaxFaces}
                    onChange={(e) => setFormMaxFaces(parseInt(e.target.value, 10) || 1)}
                    className="w-full px-3 py-2 text-xs rounded-xl bg-surface-container-low border border-border-subtle text-on-surface focus:outline-none focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-on-surface mb-1">
                    Default Text Render Mode
                  </label>
                  <select
                    value={formTextRenderMode}
                    onChange={(e) => setFormTextRenderMode(e.target.value as 'overlay' | 'baked')}
                    className="w-full px-3 py-2 text-xs rounded-xl bg-surface-container-low border border-border-subtle text-on-surface focus:outline-none focus:border-primary"
                  >
                    <option value="overlay">Overlay ( Crisp Server Composite )</option>
                    <option value="baked">Baked ( AI Renders Text In-Image )</option>
                  </select>
                </div>
              </div>

              {/* Aspect Ratio Configuration */}
              <div>
                <label className="block text-xs font-semibold text-on-surface mb-2">
                  Aspect Ratio Config
                </label>
                <div className="p-3 rounded-xl bg-surface-container-low border border-border-subtle space-y-3">
                  <p className="text-[11px] text-on-surface-variant">
                    Check which ratios are offered to users. The ★ default is pre-selected in the picker.
                  </p>

                  {/* Ratio checkboxes */}
                  <div className="grid grid-cols-2 gap-2">
                    {ASPECT_RATIOS.map((ratio) => (
                      <label
                        key={ratio}
                        className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                          formAllowedRatios.includes(ratio)
                            ? 'border-primary/40 bg-primary/5'
                            : 'border-border-subtle/50 bg-surface-container-lowest hover:border-border-subtle'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formAllowedRatios.includes(ratio)}
                          onChange={(e) => handleRatioCheck(ratio, e.target.checked)}
                          className="mt-0.5 h-3.5 w-3.5 rounded border-border-subtle text-primary focus:ring-primary"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-on-surface capitalize">{ratio}</div>
                          <div className="text-[10px] text-on-surface-variant font-mono">
                            {ratio === 'square' && '1:1 · 1080×1080'}
                            {ratio === 'portrait' && '4:5 · 1080×1350'}
                            {ratio === 'landscape' && '16:9 · 1920×1080'}
                            {ratio === 'story' && '9:16 · 1080×1920'}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>

                  {/* Default ratio radio */}
                  <div>
                    <div className="text-[11px] text-on-surface-variant font-medium mb-1.5">Default (★ pre-selected for users):</div>
                    <div className="flex flex-wrap gap-1.5">
                      {formAllowedRatios.map((ratio) => (
                        <label
                          key={ratio}
                          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium cursor-pointer transition-colors ${
                            formDefaultRatio === ratio
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border-subtle text-on-surface-variant hover:border-primary/40'
                          }`}
                        >
                          <input
                            type="radio"
                            name="defaultAspectRatio"
                            value={ratio}
                            checked={formDefaultRatio === ratio}
                            onChange={() => setFormDefaultRatio(ratio)}
                            className="sr-only"
                          />
                          {formDefaultRatio === ratio ? '★ ' : ''}{ratio}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Image Generation Provider */}
              <div>
                <label className="block text-xs font-medium text-on-surface mb-1">
                  Image Generation Provider
                </label>
                <select
                  value={formImageProvider}
                  onChange={(e) =>
                    setFormImageProvider(e.target.value as 'pulid-flux' | 'gemini' | 'openai')
                  }
                  className="w-full px-3 py-2 text-xs rounded-xl bg-surface-container-low border border-border-subtle text-on-surface focus:outline-none focus:border-primary"
                >
                  <option value="pulid-flux">PuLID-Flux ( Face-Preserving, No Text )</option>
                  <option value="gemini">Gemini 2.5 Flash Image ( Nano Banana — Text-Capable )</option>
                  <option value="openai">GPT-Image-1 ( Text-Capable )</option>
                </select>
                {formTextRenderMode === 'baked' && formImageProvider === 'pulid-flux' && (
                  <p className="mt-1 text-[11px] text-amber-500">
                    PuLID-Flux cannot render legible text — generation will silently fall back to
                    Overlay mode unless you pick Gemini or GPT-Image-1.
                  </p>
                )}
              </div>


              <div>
                <label className="block text-xs font-medium text-on-surface mb-1">
                  Thumbnail Image URL (Optional)
                </label>
                <input
                  type="text"
                  placeholder="https://cdn.invitecloud.com/ai-styles/thumb.jpg"
                  value={formThumbnailUrl}
                  onChange={(e) => setFormThumbnailUrl(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-xl bg-surface-container-low border border-border-subtle text-on-surface focus:outline-none focus:border-primary font-mono"
                />
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="activeToggle"
                  checked={formActive}
                  onChange={(e) => setFormActive(e.target.checked)}
                  className="rounded border-border-subtle text-primary focus:ring-primary h-4 w-4"
                />
                <label htmlFor="activeToggle" className="text-xs font-medium text-on-surface cursor-pointer">
                  Active (Visible to users)
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border-subtle">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-medium text-on-surface-variant hover:bg-surface-container-low"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2 rounded-xl bg-primary text-on-primary text-xs font-medium hover:bg-primary/90 disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : editingStyle ? 'Update Style' : 'Create Style'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Generation Audit Logs Modal */}
      {showLogsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-surface-container-lowest border border-border-subtle rounded-2xl w-full max-w-5xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-border-subtle bg-surface-container-low">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-emerald-500/10 text-emerald-400">
                  <Receipt className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-on-surface">AI Generation Audit Logs & Provider Costs</h3>
                  <p className="text-xs text-on-surface-variant">
                    Real-time execution log of all AI image generations, cost per call, and latency.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleOpenLogs}
                  disabled={logsLoading}
                  className="p-2 rounded-lg text-on-surface-variant hover:bg-surface-container-high transition-colors"
                  title="Refresh Logs"
                >
                  <RefreshCw className={`w-4 h-4 ${logsLoading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => setShowLogsModal(false)}
                  className="p-2 rounded-lg text-on-surface-variant hover:bg-surface-container-high transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              {logsLoading && logs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-on-surface-variant">
                  <RefreshCw className="w-8 h-8 animate-spin text-primary mb-3" />
                  <p className="text-sm">Fetching generation logs and cost metrics...</p>
                </div>
              ) : logs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-on-surface-variant">
                  <Info className="w-8 h-8 text-on-surface-variant mb-2" />
                  <p className="text-sm font-medium">No generation logs recorded yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-border-subtle">
                  <table className="w-full text-left text-xs text-on-surface">
                    <thead className="bg-surface-container-low text-on-surface-variant font-medium border-b border-border-subtle uppercase tracking-wider text-[10px]">
                      <tr>
                        <th className="px-4 py-3">Artifact ID / User</th>
                        <th className="px-4 py-3">Occasion & Style</th>
                        <th className="px-4 py-3">Provider</th>
                        <th className="px-4 py-3">Cost (USD)</th>
                        <th className="px-4 py-3">Latency</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Created</th>
                        <th className="px-4 py-3 text-right">Preview</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-subtle/50">
                      {logs.map((log) => (
                        <tr key={log.id} className="hover:bg-surface-container-low/50 transition-colors">
                          <td className="px-4 py-3 font-mono">
                            <div className="font-semibold text-on-surface text-[11px]">
                              {log.id.slice(0, 12)}...
                            </div>
                            <div className="text-[10px] text-on-surface-variant font-sans">
                              {log.user?.email || log.userId}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-on-surface">
                              {log.style?.name || log.styleId}
                            </div>
                            <div className="text-[10px] text-on-surface-variant capitalize">
                              {log.occasion} · {log.aspectRatio}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 font-mono text-[10px] font-medium">
                              {log.provider || 'pulid-flux'}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-mono font-semibold text-emerald-400">
                            ${(log.providerCost ?? 0.035).toFixed(4)}
                          </td>
                          <td className="px-4 py-3 font-mono text-on-surface-variant">
                            {log.generationTimeMs ? `${(log.generationTimeMs / 1000).toFixed(1)}s` : 'N/A'}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide inline-flex items-center gap-1 ${
                                log.status === 'READY'
                                  ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                  : log.status === 'FAILED'
                                  ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                                  : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                              }`}
                            >
                              {log.status === 'READY' && <CheckCircle2 className="w-3 h-3" />}
                              {log.status === 'FAILED' && <XCircle className="w-3 h-3" />}
                              {log.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-on-surface-variant text-[11px]">
                            {new Date(log.createdAt).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {log.resultImageUrl ? (
                              <a
                                href={log.resultImageUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-surface-container-high hover:bg-surface-container-highest text-primary text-[11px] font-medium transition-colors"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                View
                              </a>
                            ) : (
                              <span className="text-[10px] text-on-surface-variant font-mono">N/A</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-border-subtle bg-surface-container-low flex justify-between items-center text-xs text-on-surface-variant">
              <span>Showing up to 50 recent generation requests</span>
              <button
                onClick={() => setShowLogsModal(false)}
                className="px-4 py-2 rounded-xl bg-surface-container-high hover:bg-surface-container-highest text-on-surface font-medium transition-colors"
              >
                Close Audit Logs
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default AiStylesPage;
